/**
 * Importer live-grid rework — the unified **Data** step (replaces the legacy
 * Step2 sheet/header + Step3 mapping + Step4 edit). ONE spreadsheet-like grid
 * driven by the analyze report's RAW sheet content (`rawRows`, projects/v1
 * @2.13.0) so the user SEES the data while choosing the header + mapping:
 *
 *  - **Sheet selector** (top) — switching re-analyzes via the parent's
 *    `onChangeSheet` (a stateless server re-parse, no re-upload).
 *  - **Header row picked by clicking a row** — a leading radio per raw row sets
 *    that row as the header; rows ABOVE it render greyed/ignored. Changing the
 *    header re-analyzes (`onChangeHeaderRow`).
 *  - **Column mapping in each column header** — a field dropdown per column
 *    (the IMPORT_FIELD_KEYS + "(ignore)") auto-pre-filled from `report.mapping`
 *    with confidence shown; changing it triggers the debounced revalidate
 *    (`onChangeMapping`).
 *  - **Inline cell editing** on the under-header data rows — persisted to the
 *    lifted state + instant debounced re-validation (`onEditCell`; the revert
 *    is fixed BE-side). Severity tints per cell/row (cellSeverity/rowSeverity).
 *  - **Include/exclude** per data row (checkbox), the summary chips, and the
 *    needs_confirm model-confirm affordance are kept here.
 *
 * The grid renders the RAW sheet (every column, every row incl. pre-header).
 * Data rows (below the header) carry validation pulled from `report.rows`
 * (keyed by raw `rowIndex`); pre-header rows are context only.
 */
import { useCallback, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Radio,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  useGridApiRef,
  type GridCellParams,
  type GridColDef,
  type GridPaginationModel,
  type GridRenderCellParams,
} from "@mui/x-data-grid";
import { AddTurbineModelDialog } from "../../turbine-models/modal-add/AddTurbineModelDialog.js";
import { useAssetModelsForTypeQuery } from "../../turbines/queries/index.js";
import {
  IMPORT_FIELD_KEYS,
  IMPORT_FIELD_LABELS,
  REQUIRED_IMPORT_FIELDS,
  type AnalyzeImportReport,
  type ImportAnalyzedRow,
  type ImportColumnMapping,
  type ImportFieldKey,
  type ImportModelResolution,
  type ImportValidationSeverity,
} from "./types.js";
import {
  cellSeverity,
  distinctNeedsConfirmTypes,
  rowSeverity,
} from "./mapping.js";
import {
  diffFieldLabel,
  diffLine,
  diffValueText,
  mismatchFieldForColumn,
  overrideFieldKey,
  rowMismatchColumns,
  rowOverrideDiffs,
} from "./overrideDiff.js";
import {
  CircleAlertIcon,
  CircleCheckIcon,
  CircleHelpIcon,
  InfoIcon,
  NotebookPenIcon,
  PlusIcon,
  TriangleAlertIcon,
  Undo2Icon,
} from "@aeriosiq/ui/icons";

const IGNORE = "__ignore__";

const colKey = (columnIndex: number): string => `c${String(columnIndex)}`;

/** One DataGrid row backing a single RAW sheet row. `__analyzed` is the
 *  matching validated row (only for under-header data rows). */
interface GridRow {
  id: number;
  rowIndex: number;
  /** True for the header row, false for data/pre-header rows. */
  __isHeader: boolean;
  /** True for rows above the chosen header (greyed/ignored context). */
  __preHeader: boolean;
  /** The validated row (data rows below the header) — null otherwise. */
  __analyzed: ImportAnalyzedRow | null;
  [colKey: string]: unknown;
}

const SEVERITY_META: Record<
  ImportValidationSeverity,
  { label: string; color: "error" | "warning" | "info" | "default" }
> = {
  hard_error: { label: "Error", color: "error" },
  override_block: { label: "Keep existing", color: "warning" },
  needs_confirm: { label: "Confirm type", color: "info" },
  info: { label: "Info", color: "default" },
};

function SeverityIcon({
  severity,
}: {
  severity: ImportValidationSeverity;
}): ReactElement {
  switch (severity) {
    case "hard_error":
      return <CircleAlertIcon color="error" fontSize="small" />;
    case "override_block":
      return <TriangleAlertIcon color="warning" fontSize="small" />;
    case "needs_confirm":
      return <CircleHelpIcon color="info" fontSize="small" />;
    case "info":
      return <InfoIcon color="disabled" fontSize="small" />;
  }
}

function cellToString(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

export interface DataGridStepProps {
  report: AnalyzeImportReport;
  sheetName: string;
  headerRow: number;
  /** True while a sheet/header re-parse OR a revalidate is in flight. */
  isBusy: boolean;
  modelResolutions: Record<string, ImportModelResolution>;
  /** FB-30/FB-32 — tenant_admin gate (lifted from the wizard via `useHasRole`).
   *  Only a tenant admin sees the per-cell "Update Base Data" action; a
   *  non-admin gets the read-only keep-existing yellow highlight only. */
  isTenantAdmin: boolean;
  /** FB-32 — the `(rowIndex, field)` CELLS the admin chose to override (update
   *  base data from the sheet), keyed `${rowIndex}:${field}`. A marked CELL
   *  renders in the "will update" (blue) shade; un-marked mismatch cells in the
   *  same row stay yellow (keep existing). Travels into the commit body's
   *  `overrideFields`. */
  overrideFieldKeys: ReadonlySet<string>;
  /** FB-32 — toggle a single CELL's override-marked state (the clicked yellow
   *  cell's `(rowIndex, field)`), NOT the whole row. Lifted to the wizard so it
   *  travels into the commit body's `overrideFields`. */
  onToggleOverrideField: (rowIndex: number, field: string) => void;
  onChangeSheet: (sheetName: string) => void;
  onChangeHeaderRow: (headerRow: number) => void;
  onChangeMapping: (next: ImportColumnMapping[]) => void;
  onEditCell: (rowIndex: number, columnIndex: number, value: string) => void;
  onChangeIncluded: (includedRowIndexes: number[]) => void;
  onConfirmModel: (resolution: ImportModelResolution) => void;
}

export function DataGridStep({
  report,
  sheetName,
  headerRow,
  isBusy,
  modelResolutions,
  isTenantAdmin,
  overrideFieldKeys,
  onToggleOverrideField,
  onChangeSheet,
  onChangeHeaderRow,
  onChangeMapping,
  onEditCell,
  onChangeIncluded,
  onConfirmModel,
}: DataGridStepProps): ReactElement {
  const { mapping, rows, summary, rawRows, rawColumnCount } = report;

  const allModelsQuery = useAssetModelsForTypeQuery(null);
  const assetTypes = allModelsQuery.data?.assetTypes ?? [];
  const [modelDialogType, setModelDialogType] = useState<string | null>(null);

  const needsConfirmTypes = useMemo(
    () => distinctNeedsConfirmTypes(report),
    [report],
  );

  // ── Column geometry ──────────────────────────────────────────────────
  const columnCount = useMemo(() => {
    if (typeof rawColumnCount === "number" && rawColumnCount > 0) {
      return rawColumnCount;
    }
    // Fallbacks: widest raw row, else the mapping breadth.
    let max = 0;
    for (const rr of rawRows ?? []) {
      for (const c of rr.cells) max = Math.max(max, c.columnIndex + 1);
    }
    for (const m of mapping) max = Math.max(max, m.columnIndex + 1);
    return max;
  }, [rawColumnCount, rawRows, mapping]);

  const mappingByColumn = useMemo(() => {
    const m = new Map<number, ImportColumnMapping>();
    for (const c of mapping) m.set(c.columnIndex, c);
    return m;
  }, [mapping]);

  // Validated data row by its raw sheet rowIndex (for severity + edit display).
  const analyzedByRowIndex = useMemo(() => {
    const m = new Map<number, ImportAnalyzedRow>();
    for (const r of rows) m.set(r.rowIndex, r);
    return m;
  }, [rows]);

  // FB-30/FB-32 — the exact MISMATCH cells per row: the grid columns each row's
  // override-block diff fields map to (sheet ≠ existing DB). Drives the yellow
  // (keep-existing) / blue (will-update, when that CELL is override-marked) cell
  // tints + the per-cell "Update Base Data" action.
  const mismatchColumnsByRow = useMemo(() => {
    const m = new Map<number, Set<number>>();
    for (const r of rows) {
      const cols = rowMismatchColumns(r, mapping);
      if (cols.size > 0) m.set(r.rowIndex, cols);
    }
    return m;
  }, [rows, mapping]);

  // FB-32 — the per-CELL override action menu (tenant_admin only). Anchored to
  // the clicked mismatch cell; the menu marks/un-marks ONLY that (row, field).
  const [cellMenu, setCellMenu] = useState<{
    anchorEl: HTMLElement;
    rowIndex: number;
    field: string;
  } | null>(null);
  const closeCellMenu = useCallback(() => {
    setCellMenu(null);
  }, []);

  // Raw header text per column (the chosen header row's cells).
  const rawHeaderByColumn = useMemo(() => {
    const m = new Map<number, string>();
    const headerRaw = (rawRows ?? []).find((r) => r.rowIndex === headerRow);
    for (const c of headerRaw?.cells ?? []) {
      if (c.value && c.value.trim().length > 0) m.set(c.columnIndex, c.value);
    }
    return m;
  }, [rawRows, headerRow]);

  // ── Mapping change in a column header ────────────────────────────────
  const setFieldFor = useCallback(
    (columnIndex: number, value: string): void => {
      const field: ImportFieldKey | null =
        value === IGNORE ? null : (value as ImportFieldKey);
      // A field maps to EXACTLY ONE column. Before assigning it here, clear it
      // from any OTHER column that already had it — otherwise the auto-mapped
      // column AND the user's pick both claim the field, and the validator
      // reads the wrong (first) column (e.g. siteName left on "Bereich_Name"
      // while the user assigns it to "WP_kurz" → the park reads "Blatt").
      const cleared: ImportColumnMapping[] =
        field === null
          ? [...mapping]
          : mapping.map((m) =>
              m.columnIndex !== columnIndex && m.field === field
                ? { ...m, field: null, confidence: null }
                : m,
            );
      const exists = cleared.some((m) => m.columnIndex === columnIndex);
      const next: ImportColumnMapping[] = exists
        ? cleared.map((m) =>
            m.columnIndex === columnIndex
              ? { ...m, field, confidence: null }
              : m,
          )
        : [
            ...cleared,
            {
              columnIndex,
              columnHeader: rawHeaderByColumn.get(columnIndex) ?? null,
              field,
              confidence: null,
            },
          ];
      onChangeMapping(next);
    },
    [mapping, rawHeaderByColumn, onChangeMapping],
  );

  // A field mapped on >1 column → flag the duplicate.
  const fieldUsage = useMemo(() => {
    const count = new Map<string, number>();
    for (const m of mapping) {
      if (m.field) count.set(m.field, (count.get(m.field) ?? 0) + 1);
    }
    return count;
  }, [mapping]);

  // ── Columns ──────────────────────────────────────────────────────────
  const columns = useMemo<GridColDef<GridRow>[]>(() => {
    const controlCol: GridColDef<GridRow> = {
      field: "__control",
      headerName: "",
      width: 132,
      sortable: false,
      editable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Typography variant="caption" color="text.secondary">
          Header · row
        </Typography>
      ),
      renderCell: (params: GridRenderCellParams<GridRow>) => {
        const r = params.row;
        const analyzed = r.__analyzed;
        const sev = analyzed ? rowSeverity(analyzed) : null;
        return (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ width: "100%" }}
          >
            <Tooltip title="Use this row as the column-header row">
              <Radio
                size="small"
                checked={r.__isHeader}
                onClick={() => {
                  onChangeHeaderRow(r.rowIndex);
                }}
                inputProps={{
                  "aria-label": `Use row ${String(r.rowIndex + 1)} as header`,
                }}
                data-testid={`header-radio-${String(r.rowIndex)}`}
              />
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              {r.rowIndex + 1}
            </Typography>
            {analyzed ? (
              sev === null ? (
                <Chip
                  size="small"
                  variant="outlined"
                  color="success"
                  label="OK"
                  data-testid={`row-status-${String(r.rowIndex)}`}
                />
              ) : (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  <Tooltip
                    title={
                      <Box sx={{ whiteSpace: "pre-line" }}>
                        {analyzed.messages
                          .map((msg) => `${msg.severity}: ${msg.message}`)
                          .join("\n")}
                      </Box>
                    }
                  >
                    <Chip
                      size="small"
                      color={SEVERITY_META[sev].color}
                      icon={<SeverityIcon severity={sev} />}
                      label={SEVERITY_META[sev].label}
                      data-testid={`row-status-${String(r.rowIndex)}`}
                    />
                  </Tooltip>
                  {/* FB-27: show WHICH parameter differs inline on the
                      would-change row (no dialog needed). */}
                  {sev === "override_block"
                    ? (() => {
                        const diffs = rowOverrideDiffs(analyzed);
                        if (diffs.length === 0) return null;
                        return (
                          <Tooltip
                            title={
                              <Box sx={{ whiteSpace: "pre-line" }}>
                                {diffs.map((d) => diffLine(d)).join("\n")}
                              </Box>
                            }
                          >
                            <Typography
                              variant="caption"
                              color="warning.main"
                              noWrap
                              sx={{ maxWidth: 180 }}
                              data-testid={`row-diff-${String(r.rowIndex)}`}
                            >
                              {diffs
                                .map((d) => diffFieldLabel(d.field))
                                .join(", ")}
                            </Typography>
                          </Tooltip>
                        );
                      })()
                    : null}
                  {/* FB-32 — once an admin marks ANY of this override_block
                      row's mismatch cells to override, show a "Will update"
                      chip. The chip names the per-FIELD marks (e.g. "Will
                      update: Park name") so the user sees which cells, not the
                      whole row. */}
                  {sev === "override_block"
                    ? (() => {
                        const markedFields = rowOverrideDiffs(analyzed)
                          .map((d) => d.field)
                          .filter((f, i, arr) => arr.indexOf(f) === i)
                          .filter((f) =>
                            overrideFieldKeys.has(
                              overrideFieldKey(r.rowIndex, f),
                            ),
                          );
                        if (markedFields.length === 0) return null;
                        return (
                          <Chip
                            size="small"
                            color="info"
                            variant="outlined"
                            icon={<NotebookPenIcon fontSize="small" />}
                            label={`Will update: ${markedFields
                              .map((f) => diffFieldLabel(f))
                              .join(", ")}`}
                            data-testid={`row-override-${String(r.rowIndex)}`}
                          />
                        );
                      })()
                    : null}
                </Stack>
              )
            ) : null}
          </Stack>
        );
      },
    };

    const includeCol: GridColDef<GridRow> = {
      field: "__include",
      headerName: "Import",
      width: 78,
      sortable: false,
      editable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<GridRow>) => {
        const analyzed = params.row.__analyzed;
        if (analyzed === null) return null;
        return (
          <input
            type="checkbox"
            checked={analyzed.included}
            aria-label={`Include row ${String(params.row.rowIndex + 1)}`}
            data-testid={`include-${String(params.row.rowIndex)}`}
            onChange={(e) => {
              const next = rows
                .filter((rr) =>
                  rr.rowIndex === analyzed.rowIndex
                    ? e.target.checked
                    : rr.included,
                )
                .map((rr) => rr.rowIndex);
              onChangeIncluded(next);
            }}
          />
        );
      },
    };

    const dataCols: GridColDef<GridRow>[] = Array.from(
      { length: columnCount },
      (_v, columnIndex): GridColDef<GridRow> => {
        const m = mappingByColumn.get(columnIndex);
        const mapped = (m?.field ?? null) as ImportFieldKey | null;
        const dup = mapped !== null && (fieldUsage.get(mapped) ?? 0) > 1;
        return {
          field: colKey(columnIndex),
          headerName: rawHeaderByColumn.get(columnIndex) ?? "",
          flex: 1,
          minWidth: 170,
          editable: true,
          sortable: false,
          disableColumnMenu: true,
          // The mapping dropdown lives IN the column header.
          renderHeader: () => (
            <Stack spacing={0.25} sx={{ py: 0.5, width: "100%" }}>
              <Typography
                variant="caption"
                noWrap
                sx={{ fontWeight: 600 }}
                title={rawHeaderByColumn.get(columnIndex) ?? ""}
              >
                {rawHeaderByColumn.get(columnIndex) ??
                  `Column ${String(columnIndex + 1)}`}
              </Typography>
              <Select
                size="small"
                value={mapped ?? IGNORE}
                error={dup}
                variant="standard"
                onChange={(e) => {
                  setFieldFor(columnIndex, e.target.value);
                }}
                inputProps={{
                  "aria-label": `Maps column ${String(columnIndex + 1)} to`,
                }}
                data-testid={`col-map-${String(columnIndex)}`}
                sx={{ fontSize: "0.78rem" }}
              >
                <MenuItem value={IGNORE}>
                  <em>(ignore)</em>
                </MenuItem>
                {IMPORT_FIELD_KEYS.map((f) => (
                  <MenuItem key={f} value={f}>
                    {IMPORT_FIELD_LABELS[f]}
                    {REQUIRED_IMPORT_FIELDS.includes(f) ? " *" : ""}
                  </MenuItem>
                ))}
              </Select>
              {typeof m?.confidence === "number" ? (
                <Typography variant="caption" color="text.secondary">
                  auto {Math.round(m.confidence * 100)}%
                </Typography>
              ) : null}
            </Stack>
          ),
          cellClassName: (params) => {
            const r = params.row;
            if (r.__preHeader) return "import-cell-prehdr";
            if (r.__isHeader) return "import-cell-header";
            if (mapped === null) return "import-cell-ignored";
            const analyzed = r.__analyzed;
            if (analyzed === null) return "";
            // FB-30/FB-32 — a MISMATCH cell (this column is one of the row's
            // override-block diff fields). Highlight it distinctly from the
            // generic row/severity tint: yellow = keep-existing (default),
            // blue = will-update (the admin marked THIS cell to override). The
            // mark is PER-CELL — a sibling mismatch cell in the same row stays
            // yellow unless it too is marked. A tenant_admin's mismatch cell is
            // clickable (cursor) → the action.
            const isMismatch =
              mismatchColumnsByRow.get(r.rowIndex)?.has(columnIndex) ?? false;
            if (isMismatch) {
              const field = mismatchFieldForColumn(
                analyzed,
                mapping,
                columnIndex,
              );
              const marked =
                field !== null &&
                overrideFieldKeys.has(overrideFieldKey(r.rowIndex, field));
              const base = marked
                ? "import-cell-will-update"
                : "import-cell-mismatch";
              return isTenantAdmin ? `${base} import-cell-overridable` : base;
            }
            const sev = cellSeverity(analyzed, mapped);
            return sev !== null ? `import-cell-${sev}` : "";
          },
        };
      },
    );

    return [controlCol, includeCol, ...dataCols];
  }, [
    columnCount,
    mapping,
    mappingByColumn,
    rawHeaderByColumn,
    fieldUsage,
    rows,
    mismatchColumnsByRow,
    overrideFieldKeys,
    isTenantAdmin,
    onChangeHeaderRow,
    onChangeIncluded,
    setFieldFor,
  ]);

  // ── Rows (the raw sheet) ─────────────────────────────────────────────
  // FB-26: the analyze response caps `rawRows` (BE MAX_RAW_ROWS=200) but NOT
  // the validated `rows`. An analyzed row (incl. a hard_error) whose raw
  // `rowIndex` is past the cap has no `rawRows` entry — without this it never
  // gets a grid row and the user can't find it ("2 errors but the rows are
  // hidden"). We render those orphaned analyzed rows from the analyzed cells
  // so EVERY validated row is reachable, never silently dropped.
  const gridRows = useMemo<GridRow[]>(() => {
    const source = rawRows ?? [];
    const rawIndexes = new Set(source.map((rr) => rr.rowIndex));
    const rawBacked = source.map((rr) => {
      const isHeader = rr.rowIndex === headerRow;
      const preHeader = rr.rowIndex < headerRow;
      const analyzed =
        !isHeader && !preHeader
          ? (analyzedByRowIndex.get(rr.rowIndex) ?? null)
          : null;
      const gr: GridRow = {
        id: rr.rowIndex,
        rowIndex: rr.rowIndex,
        __isHeader: isHeader,
        __preHeader: preHeader,
        __analyzed: analyzed,
      };
      // Prefer the validated (post-edit) cell value for data rows so an inline
      // edit shows live; fall back to the raw cell otherwise.
      for (let ci = 0; ci < columnCount; ci += 1) {
        const raw = rr.cells.find((c) => c.columnIndex === ci)?.value ?? "";
        const edited =
          analyzed?.cells.find((c) => c.columnIndex === ci)?.value ?? null;
        gr[colKey(ci)] = edited ?? raw;
      }
      return gr;
    });

    // Orphaned analyzed rows (no raw row — beyond the raw cap): synthesise a
    // grid row from the analyzed cells so they render (FB-26). Skip any that
    // fall in the pre-header zone (defensive — analyzed rows are below header).
    const orphans: GridRow[] = [];
    for (const r of rows) {
      if (rawIndexes.has(r.rowIndex) || r.rowIndex <= headerRow) continue;
      const gr: GridRow = {
        id: r.rowIndex,
        rowIndex: r.rowIndex,
        __isHeader: false,
        __preHeader: false,
        __analyzed: r,
      };
      for (let ci = 0; ci < columnCount; ci += 1) {
        gr[colKey(ci)] = r.cells.find((c) => c.columnIndex === ci)?.value ?? "";
      }
      orphans.push(gr);
    }

    return [...rawBacked, ...orphans].sort((a, b) => a.rowIndex - b.rowIndex);
  }, [rawRows, rows, headerRow, analyzedByRowIndex, columnCount]);

  const includedCount = rows.filter((r) => r.included).length;

  // ── FB-26: jump-to-errors affordance ─────────────────────────────────
  // Controlled pagination so "jump to errors" can page to the first erroring
  // row even when it sits past page 1, and an apiRef so we can scroll it into
  // view + select it. PAGE_SIZE matches the grid below.
  const PAGE_SIZE = 100;
  const apiRef = useGridApiRef();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });

  // The grid positions (index into the rendered `gridRows`) of every row whose
  // analyzed status is a hard_error — the rows the user must SEE (FB-26).
  const errorRowPositions = useMemo(() => {
    const positions: number[] = [];
    gridRows.forEach((gr, i) => {
      const a = gr.__analyzed;
      if (a && rowSeverity(a) === "hard_error") positions.push(i);
    });
    return positions;
  }, [gridRows]);

  const jumpToError = useCallback(
    (which: "first" | "next") => {
      if (errorRowPositions.length === 0) return;
      // Pick the next error after the current top-of-page, wrapping around, so
      // repeated clicks cycle through ALL error rows.
      const fromPos = paginationModel.page * paginationModel.pageSize;
      let target = errorRowPositions[0] ?? 0;
      if (which === "next") {
        const ahead = errorRowPositions.find((p) => p > fromPos);
        target = ahead ?? errorRowPositions[0] ?? 0;
      }
      const targetRow = gridRows[target];
      setPaginationModel((m) => ({
        ...m,
        page: Math.floor(target / m.pageSize),
      }));
      // Scroll the row into view after the page settles.
      window.setTimeout(() => {
        try {
          apiRef.current.scrollToIndexes({ rowIndex: target, colIndex: 0 });
          if (targetRow) apiRef.current.selectRow(targetRow.id, true, true);
        } catch {
          /* apiRef not ready — the page change alone surfaces the row */
        }
      }, 0);
    },
    [errorRowPositions, gridRows, paginationModel, apiRef],
  );

  const processRowUpdate = useCallback(
    (newRow: GridRow, oldRow: GridRow): GridRow => {
      // Pre-header / header rows are not editable data — ignore any change.
      if (newRow.__analyzed === null) return oldRow;
      for (let ci = 0; ci < columnCount; ci += 1) {
        const key = colKey(ci);
        if (newRow[key] !== oldRow[key]) {
          onEditCell(newRow.rowIndex, ci, cellToString(newRow[key]));
        }
      }
      return newRow;
    },
    [columnCount, onEditCell],
  );

  // FB-32 — a tenant_admin clicking a MISMATCH cell opens the "Update Base
  // Data" action menu (anchored to the cell). Non-admins get no action — the
  // single click just selects/edits the cell as usual. The menu marks/un-marks
  // ONLY the clicked CELL's (rowIndex, field) for override — its siblings are
  // untouched. The clicked column is resolved to its override-block diff field.
  const handleCellClick = useCallback(
    (params: GridCellParams<GridRow>, event: React.MouseEvent) => {
      if (!isTenantAdmin) return;
      const r = params.row;
      const analyzed = r.__analyzed;
      if (analyzed === null) return;
      const columnIndex = Number(params.field.replace(/^c/, ""));
      if (Number.isNaN(columnIndex)) return;
      const field = mismatchFieldForColumn(analyzed, mapping, columnIndex);
      if (field === null) return;
      setCellMenu({
        anchorEl: event.currentTarget as HTMLElement,
        rowIndex: r.rowIndex,
        field,
      });
    },
    [isTenantAdmin, mapping],
  );

  const mappedRequired = REQUIRED_IMPORT_FIELDS.filter((f) =>
    mapping.some((m) => m.field === f),
  );
  const missingRequired = REQUIRED_IMPORT_FIELDS.filter(
    (f) => !mappedRequired.includes(f),
  );

  const sheets = report.sheets;

  return (
    <Stack spacing={1.5}>
      {/* ── Sheet selector + summary ─────────────────────────────────── */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <TextField
          select
          size="small"
          label="Data sheet"
          value={sheetName}
          onChange={(e) => {
            onChangeSheet(e.target.value);
          }}
          disabled={isBusy}
          sx={{ minWidth: 220 }}
          slotProps={{ htmlInput: { "aria-label": "Data sheet" } }}
        >
          {sheets.map((s) => (
            <MenuItem key={s.sheetName} value={s.sheetName}>
              {s.sheetName}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                ({s.rowCount} rows)
              </Typography>
            </MenuItem>
          ))}
        </TextField>

        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="import-grid-summary"
        >
          {summary.totalRows} rows · {includedCount} included
        </Typography>
        {summary.hardErrorRows > 0 ? (
          <Chip
            size="small"
            color="error"
            icon={<CircleAlertIcon fontSize="small" />}
            label={`${String(summary.hardErrorRows)} blocking`}
          />
        ) : null}
        {summary.overrideBlockRows > 0 ? (
          <Tooltip title="These match an existing turbine with different values. They import keeping your existing base data; a tenant admin can override to update it from the sheet.">
            <Chip
              size="small"
              color="warning"
              icon={<TriangleAlertIcon fontSize="small" />}
              label={`${String(
                summary.overrideBlockRows,
              )} keep existing (differs)`}
            />
          </Tooltip>
        ) : null}
        {summary.needsConfirmRows > 0 ? (
          <Chip
            size="small"
            color="info"
            icon={<CircleHelpIcon fontSize="small" />}
            label={`${String(summary.needsConfirmRows)} need a model`}
          />
        ) : null}
        {isBusy ? (
          <Typography variant="caption" color="text.secondary">
            Re-validating…
          </Typography>
        ) : null}
      </Stack>

      <Typography variant="caption" color="text.secondary">
        Click the radio on the row that holds the column headers, map each
        column from its header dropdown, and edit any cell inline. Rows above
        the header are ignored.
      </Typography>

      {missingRequired.length > 0 ? (
        <Alert
          severity="warning"
          variant="outlined"
          data-testid="missing-required"
        >
          Not all required fields are mapped yet:{" "}
          {missingRequired.map((f) => IMPORT_FIELD_LABELS[f]).join(", ")}.
        </Alert>
      ) : null}

      {/* FB-26: a visible error banner that jumps the grid to the erroring
          rows so they're never hidden (past the page or the raw-row cap). */}
      {errorRowPositions.length > 0 ? (
        <Alert
          severity="error"
          variant="outlined"
          data-testid="error-jump-banner"
          action={
            <Button
              color="error"
              size="small"
              startIcon={<CircleAlertIcon fontSize="small" />}
              onClick={() => {
                jumpToError("next");
              }}
              data-testid="jump-to-errors"
            >
              {errorRowPositions.length === 1
                ? "Show the error row"
                : "Show error rows"}
            </Button>
          }
        >
          {`${String(errorRowPositions.length)} row${
            errorRowPositions.length === 1 ? "" : "s"
          } ${
            errorRowPositions.length === 1 ? "has" : "have"
          } a blocking error and must be fixed or excluded before importing.`}
        </Alert>
      ) : null}

      {/* ── The live grid ────────────────────────────────────────────── */}
      <Box
        sx={{
          height: 460,
          width: "100%",
          "& .import-cell-prehdr": {
            color: "text.disabled",
            fontStyle: "italic",
          },
          "& .import-cell-header": (t) => ({
            fontWeight: 700,
            bgcolor:
              t.palette.mode === "dark"
                ? "rgba(255,255,255,0.06)"
                : "rgba(0,0,0,0.04)",
          }),
          "& .import-cell-hard_error": (t) => ({
            bgcolor:
              t.palette.mode === "dark"
                ? "rgba(255,69,58,0.18)"
                : "rgba(255,59,48,0.10)",
          }),
          "& .import-cell-override_block": (t) => ({
            bgcolor:
              t.palette.mode === "dark"
                ? "rgba(255,159,10,0.18)"
                : "rgba(255,149,0,0.12)",
          }),
          "& .import-cell-needs_confirm": (t) => ({
            bgcolor:
              t.palette.mode === "dark"
                ? "rgba(10,132,255,0.16)"
                : "rgba(0,122,255,0.08)",
          }),
          // FB-30 — the specific MISMATCH cell (a per-field override-block
          // diff): a stronger, distinct YELLOW so the user sees exactly which
          // cell differs (keep-existing default).
          "& .import-cell-mismatch": (t) => ({
            bgcolor:
              t.palette.mode === "dark"
                ? "rgba(255,214,10,0.26)"
                : "rgba(255,204,0,0.30)",
            fontWeight: 600,
          }),
          // FB-30 — a mismatch cell on a row the admin marked to override:
          // shifts yellow → BLUE ("will update base data from the sheet").
          "& .import-cell-will-update": (t) => ({
            bgcolor:
              t.palette.mode === "dark"
                ? "rgba(10,132,255,0.30)"
                : "rgba(0,122,255,0.20)",
            fontWeight: 600,
          }),
          // FB-30 — a tenant_admin's mismatch cell is clickable (the override
          // action). Pointer cursor signals the affordance.
          "& .import-cell-overridable": { cursor: "pointer" },
          "& .import-cell-ignored": { color: "text.disabled" },
          "& .import-row-prehdr": { opacity: 0.55 },
        }}
      >
        <DataGrid<GridRow>
          apiRef={apiRef}
          aria-label="Import data"
          slotProps={{ main: { "aria-label": "Import data" } }}
          rows={gridRows}
          columns={columns}
          getRowId={(row) => row.id}
          getRowClassName={(params) =>
            params.row.__preHeader ? "import-row-prehdr" : ""
          }
          columnHeaderHeight={92}
          density="compact"
          disableRowSelectionOnClick
          processRowUpdate={processRowUpdate}
          isCellEditable={(params) =>
            (params.row as GridRow).__analyzed !== null
          }
          onCellClick={handleCellClick}
          onProcessRowUpdateError={() => undefined}
          disableColumnMenu
          pageSizeOptions={[PAGE_SIZE]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
        />
      </Box>

      {/* FB-32 — the per-CELL override action menu (tenant_admin only),
          anchored to the clicked mismatch cell. Marks/un-marks ONLY that
          (row, field) for override (update base data from the sheet vs keep
          existing) — its sibling mismatch cells are untouched. */}
      <Menu
        open={cellMenu !== null}
        anchorEl={cellMenu?.anchorEl ?? null}
        onClose={closeCellMenu}
        data-testid="override-cell-menu"
      >
        {cellMenu !== null
          ? (() => {
              const marked = overrideFieldKeys.has(
                overrideFieldKey(cellMenu.rowIndex, cellMenu.field),
              );
              const label = diffFieldLabel(cellMenu.field);
              // FB-33 — show the user WHAT they'd overwrite: the current
              // database value vs the incoming sheet value for this exact cell
              // (from the row's override diff), so the override decision is
              // informed rather than blind.
              const analyzed = analyzedByRowIndex.get(cellMenu.rowIndex);
              const diff = analyzed
                ? rowOverrideDiffs(analyzed).find(
                    (d) => d.field === cellMenu.field,
                  )
                : undefined;
              return (
                <>
                  <Box sx={{ px: 2, py: 1, maxWidth: 360 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 0.5 }}
                    >
                      {label}
                    </Typography>
                    {diff ? (
                      <>
                        <Typography variant="body2" data-testid="menu-db-value">
                          <Box
                            component="span"
                            sx={{ color: "text.secondary" }}
                          >
                            Database:{" "}
                          </Box>
                          {diffValueText(diff.current)}
                        </Typography>
                        <Typography
                          variant="body2"
                          data-testid="menu-sheet-value"
                        >
                          <Box
                            component="span"
                            sx={{ color: "text.secondary" }}
                          >
                            Sheet:{" "}
                          </Box>
                          {diffValueText(diff.incoming)}
                        </Typography>
                      </>
                    ) : null}
                  </Box>
                  <Divider />
                  <MenuItem
                    data-testid="override-action"
                    onClick={() => {
                      onToggleOverrideField(cellMenu.rowIndex, cellMenu.field);
                      closeCellMenu();
                    }}
                  >
                    <ListItemIcon>
                      {marked ? (
                        <Undo2Icon fontSize="small" />
                      ) : (
                        <NotebookPenIcon fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText>
                      {marked
                        ? `Keep existing ${label}`
                        : `Update ${label} from sheet`}
                    </ListItemText>
                  </MenuItem>
                </>
              );
            })()
          : null}
      </Menu>

      {/* ── Model-resolution confirm (needs_confirm) ─────────────────── */}
      {needsConfirmTypes.length > 0 ? (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            New system types
          </Typography>
          <Typography variant="caption" color="text.secondary">
            These system types are not in the catalogue. Confirm a new model for
            each before importing.
          </Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {needsConfirmTypes.map((rawType) => {
              const confirmed = modelResolutions[rawType] !== undefined;
              return (
                <Paper
                  key={rawType}
                  variant="outlined"
                  sx={{ p: 1.5 }}
                  data-testid={`needs-confirm-${rawType}`}
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2">
                      <strong>{rawType}</strong>
                    </Typography>
                    {confirmed ? (
                      <Chip
                        size="small"
                        color="success"
                        icon={<CircleCheckIcon />}
                        label="Will create new model"
                        data-testid={`confirmed-${rawType}`}
                      />
                    ) : (
                      <Button
                        size="small"
                        startIcon={<PlusIcon fontSize="small" />}
                        onClick={() => {
                          setModelDialogType(rawType);
                        }}
                      >
                        Create new model
                      </Button>
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      ) : null}

      <AddTurbineModelDialog
        open={modelDialogType !== null}
        assetTypes={assetTypes}
        onClose={() => {
          if (modelDialogType !== null) {
            onConfirmModel({
              rawType: modelDialogType,
              action: "create_new",
              newModelName: modelDialogType,
            });
          }
          setModelDialogType(null);
        }}
      />
    </Stack>
  );
}
